import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI, reservationsAPI, stockAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

/**
 * ActivityFeed — Dashboard'da KPI kartlarının altında gösterilen 3 kolonlu
 * canlı durum özeti:
 *  • Son aktif siparişler (preparing/ready) — max 4
 *  • Bugünkü rezervasyonlar (pending/confirmed) — max 4
 *  • Kritik stok uyarıları — max 4
 *
 * Her kolon tıklanabilir "Tümünü gör" link'i barındırır.
 * Veriler Promise.allSettled ile paralel çekilir — tek endpoint fail ederse
 * diğer kolonlar hâlâ görünür.
 */
export default function ActivityFeed() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [recentOrders, setRecentOrders] = useState([]);
  const [todayReservations, setTodayReservations] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    Promise.allSettled([
      ordersAPI.getAll({ status: 'preparing' }),
      reservationsAPI.getAll({ date: today }),
      stockAPI.getLowStock(),
    ])
      .then(([ordRes, resRes, stockRes]) => {
        if (ordRes.status === 'fulfilled') {
          const arr = ordRes.value.data?.data || [];
          setRecentOrders(arr.slice(0, 4));
        }
        if (resRes.status === 'fulfilled') {
          const arr = resRes.value.data?.data || [];
          setTodayReservations(arr.filter(r => ['pending', 'confirmed'].includes(r.status)).slice(0, 4));
        }
        if (stockRes.status === 'fulfilled') {
          const arr = stockRes.value.data?.data || [];
          setLowStock(arr.slice(0, 4));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const Column = ({ icon, title, navTo, items, emptyKey, render, navLabel }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={() => navigate(navTo)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg" aria-hidden="true">{icon}</span>
          <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm truncate">{title}</span>
          {items.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-bold shrink-0">
              {items.length}
            </span>
          )}
        </div>
        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium shrink-0 ml-2">
          {navLabel} →
        </span>
      </button>
      <div className="divide-y divide-gray-100 dark:divide-gray-700/70">
        {loading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="px-4 py-3 animate-pulse">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-1.5" />
              <div className="h-2.5 bg-gray-100 dark:bg-gray-700/50 rounded w-1/2" />
            </div>
          ))
        ) : items.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-gray-400 dark:text-gray-500">
            {t(emptyKey)}
          </div>
        ) : (
          items.map(render)
        )}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      <Column
        icon="🧾"
        title={t('activityRecentOrders')}
        navTo="/orders"
        items={recentOrders}
        emptyKey="activityNoOrders"
        navLabel={t('activitySeeAll')}
        render={(o) => (
          <div key={o.id} className="px-4 py-2.5 flex justify-between items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                {o.table_name ? `🪑 ${o.table_name}` : o.source || '—'}
              </div>
              <div className="text-[11px] text-gray-400 dark:text-gray-500">
                #{o.id} · {(o.items || []).length} {t('activityItemsUnit')}
              </div>
            </div>
            <div className="text-sm font-bold text-gray-800 dark:text-gray-200 shrink-0">
              ₺{parseFloat(o.total_amount || 0).toFixed(2)}
            </div>
          </div>
        )}
      />

      <Column
        icon="📅"
        title={t('activityTodayReservations')}
        navTo="/reservations"
        items={todayReservations}
        emptyKey="activityNoReservations"
        navLabel={t('activitySeeAll')}
        render={(r) => (
          <div key={r.id} className="px-4 py-2.5 flex justify-between items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                {r.customer_name || '—'}
              </div>
              <div className="text-[11px] text-gray-400 dark:text-gray-500">
                {r.reservation_time?.slice(0, 5)} · {r.party_size} {t('activityGuestsUnit')}
              </div>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${
              r.status === 'confirmed'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
            }`}>
              {r.status === 'confirmed' ? '✓' : '⋯'}
            </span>
          </div>
        )}
      />

      <Column
        icon="⚠️"
        title={t('activityLowStock')}
        navTo="/stock"
        items={lowStock}
        emptyKey="activityNoLowStock"
        navLabel={t('activitySeeAll')}
        render={(s) => (
          <div key={s.id} className="px-4 py-2.5 flex justify-between items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                {s.name}
              </div>
              <div className="text-[11px] text-gray-400 dark:text-gray-500">
                {t('activityCriticalLevel')}: {parseFloat(s.critical_level).toFixed(1)} {s.unit}
              </div>
            </div>
            <div className="text-sm font-bold text-red-600 dark:text-red-400 shrink-0">
              {parseFloat(s.quantity).toFixed(1)}
            </div>
          </div>
        )}
      />
    </div>
  );
}
